import {ReactElement, ReactNode} from "react";
import {FormattedMessage} from "react-intl";
import {EditorGroup, EditorGroupTitle} from "./EditorGroup";
import {SubTitle} from "components/UIComponents/SubTitle";
import {ReactComponent as A4Icon} from "components/Icons/a4.svg";
import {ReactComponent as A4HalfIcon} from "components/Icons/a4half.svg";
import {ReactComponent as A5Icon} from "components/Icons/a5.svg";
import {Checkbox} from "components/UIComponents/Checkbox";
import {SliderToggle} from "components/UIComponents/SliderToggle";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {menuEditorActions, menuEditorSelectors} from "../editorSlice";
import {MenuFormat, PrintMode} from "../MenuDocumentModel";
import {createEmptyCoverPage} from "../editorUtils";
import {SecondaryButton} from "../../../components/UIComponents/SecondaryButton";
import {authSelectors} from "../../auth/authSlice";

export interface FormatEditorProps {
}

export function FormatEditor(props: FormatEditorProps): ReactElement | null {
    const dispatch = useAppDispatch();

    const document = useAppSelector(menuEditorSelectors.document);
    const isSalesUser = useAppSelector(authSelectors.isSalesUser);

    if (document == null) {
        return null;
    }


    const setIsDegu = (isDegu: boolean) => dispatch(menuEditorActions.setIsDegu(isDegu));
    const setDisplayFooter = (displayFooter: boolean) => dispatch(menuEditorActions.setDisplayFooter(displayFooter));
    const setShowAppellation = (appellation: boolean) => dispatch(menuEditorActions.setShowAppellation(appellation));
    const setShowYear = (show: boolean) => dispatch(menuEditorActions.setShowYear(show));

    const setPrintMode = (printMode: PrintMode) => dispatch(menuEditorActions.setPrintMode(printMode));
    const setCoverPageEnabled = (enableCoverpage: boolean) => {
        const coverPage = enableCoverpage ? createEmptyCoverPage() : null;
        dispatch(menuEditorActions.setCoverPage(coverPage));
    }
    const setFormat = (format: MenuFormat) => dispatch(menuEditorActions.setFormat(format));
    const setAllDetailed = (detailed: boolean) => dispatch(menuEditorActions.setAllWinesDetailed(detailed));
    const setGlobalSuitableFood = (globalSuitableFood: boolean) => dispatch(menuEditorActions.setGlobalSuitableFood(globalSuitableFood));

    return (
        <div className="flex flex-col">
            <div className="px-6">
                <SubTitle><FormattedMessage id="editor.format"/></SubTitle>
            </div>

            <EditorGroup>
                <EditorGroupTitle><FormattedMessage id="editor.format.wines"/></EditorGroupTitle>
                <div className="flex gap-2">
                    <div>
                        <SecondaryButton onClick={() => setAllDetailed(true)}>
                            <FormattedMessage id="editor.format.detailed"/>
                        </SecondaryButton>
                    </div>
                    <div>
                        <SecondaryButton onClick={() => setAllDetailed(false)}>
                            <FormattedMessage id="editor.format.simple"/>
                        </SecondaryButton>
                    </div>
                </div>
                <div className="flex items-center gap-3 pt-2">
                    <div className="flex-auto">
                        <EditorGroupTitle><FormattedMessage id="editor.format.showAppellation"/></EditorGroupTitle>
                    </div>
                    <div className="flex-none h-7 w-12">
                        <SliderToggle
                            enabled={document.showAppellation}
                            setEnabled={setShowAppellation}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3 pt-2">
                    <div className="flex-auto">
                        <EditorGroupTitle><FormattedMessage id="editor.format.showYear"/></EditorGroupTitle>
                    </div>
                    <div className="flex-none h-7 w-12">
                        <SliderToggle
                            enabled={!(document.showYear===false)}
                            setEnabled={setShowYear}
                        />
                    </div>
                </div>

            </EditorGroup>

            <EditorGroup>
                <EditorGroupTitle><FormattedMessage id="editor.format.suitableFood"/></EditorGroupTitle>
                <div className="flex gap-2">
                    <div>
                        <SecondaryButton onClick={() => setGlobalSuitableFood(true)}>
                            <FormattedMessage id="editor.format.suitableFood.enabled"/>
                        </SecondaryButton>
                    </div>
                    <div>
                        <SecondaryButton onClick={() => setGlobalSuitableFood(false)}>
                            <FormattedMessage id="editor.format.suitableFood.disabled"/>
                        </SecondaryButton>
                    </div>
                </div>
            </EditorGroup>

            <EditorGroup>
                <EditorGroupTitle><FormattedMessage id="editor.format.papersize"/></EditorGroupTitle>
                <div className="flex items-baseline justify-between pr-2 font-barlow">
                    <FormatSelectorItem
                        currentlySelectedFormat={document.format}
                        onSelect={setFormat}
                        format="a4"
                        icon={<A4Icon/>}
                    >
                        A4
                    </FormatSelectorItem>

                    <FormatSelectorItem
                        currentlySelectedFormat={document.format}
                        onSelect={setFormat}
                        format="a4-half"
                        icon={<A4HalfIcon/>}
                    >
                        1/2 A4
                    </FormatSelectorItem>

                    <FormatSelectorItem
                        currentlySelectedFormat={document.format}
                        onSelect={setFormat}
                        format="a5"
                        icon={<A5Icon/>}
                    >
                        A5
                    </FormatSelectorItem>
                </div>
            </EditorGroup>

            <EditorGroup>
                <div className="flex items-center gap-3">
                    <div className="flex-auto">
                        <EditorGroupTitle><FormattedMessage id="editor.format.coverPage"/></EditorGroupTitle>
                    </div>
                    <div className="flex-none h-7 w-12">
                        <SliderToggle
                            enabled={document.coverPage != null}
                            setEnabled={setCoverPageEnabled}
                        />
                    </div>
                </div>
            </EditorGroup>

            <EditorGroup>
                <EditorGroupTitle><FormattedMessage id="editor.format.print"/></EditorGroupTitle>
                <div className="flex flex-col gap-4">
                    <button className="flex items-center gap-3"
                            onClick={() => setPrintMode("singlepages")}>
                        <span className="h-4">
                            <Checkbox checked={document.printMode === "singlepages"}/>
                        </span>
                        <span className="flex"><FormattedMessage id="editor.format.singlePages"/></span>
                    </button>
                    <button className="flex items-center gap-3"
                            onClick={() => setPrintMode("brochure")}>
                        <span className="h-4">
                            <Checkbox checked={document.printMode === "brochure"}/>
                        </span>
                        <span className="flex"><FormattedMessage id="editor.format.brochure"/></span>
                    </button>
                </div>
            </EditorGroup>

            <EditorGroup>
                <div className="flex items-center gap-3">
                    <div className="flex-auto">
                        <EditorGroupTitle><FormattedMessage id="editor.format.footer"/></EditorGroupTitle>
                    </div>
                    <div className="flex-none h-7 w-12">
                        <SliderToggle
                            enabled={document.displayFooter}
                            setEnabled={setDisplayFooter}
                        />
                    </div>
                </div>
            </EditorGroup>

            {isSalesUser && (
                <EditorGroup>
                    <div className="flex items-center gap-3">
                        <div className="flex-auto">
                            <EditorGroupTitle><FormattedMessage id="editor.format.degu"/></EditorGroupTitle>
                        </div>
                        <div className="flex-none h-7 w-12">
                            <SliderToggle
                                enabled={document.degu}
                                setEnabled={setIsDegu}
                            />
                        </div>
                    </div>
                    <ul className="text-light-text text-sm leading-tight font-fira-sans
                               list-disc list-outside pl-5">
                        <li><FormattedMessage id="editor.format.deguHelpTextLine1"/></li>
                        <li><FormattedMessage id="editor.format.deguHelpTextLine2"/></li>
                    </ul>
                </EditorGroup>
            )}
        </div>
    );
}

interface FormatSelectorItemProps {
    currentlySelectedFormat: MenuFormat
    onSelect: (format: MenuFormat) => void
    format: MenuFormat
    icon: ReactNode
    children: ReactNode
    disabled?: boolean
}

function FormatSelectorItem(props: FormatSelectorItemProps): ReactElement {
    const isSelected = props.format === props.currentlySelectedFormat;
    return (
        <button className="flex flex-col items-center gap-2 w-20 disabled:opacity-30"
                disabled={props.disabled}
                onClick={() => {
                    props.onSelect(props.format);
                }}>
            <div>{props.icon}</div>
            <div className="flex items-center gap-1">
                {(isSelected) && (
                    <span className="inline-block h-4">
                        <Checkbox checked={true}/>
                    </span>
                )}
                <span className={isSelected ? "font-bold" : ""}>{props.children}</span>
            </div>
        </button>
    );
}